import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import axios from 'axios'
import { DateTime } from 'luxon'

export const useUserStore = defineStore('rdUser', {
  state: () => ({
    user: useStorage('user', {}),
    jwt: useStorage('jwt', ''),
    invalidIn: useStorage('invalidIn', null),
  }),

  getters: {
    getUserAvatarUrl() {
      return `${import.meta.env.VITE_API_URL}user/avatar/${this.user._id}`
    },
    getUser() {
      return this.user
    },
    getTokenHeader() {
      return { Authorization: `Bearer ${this.jwt}` }
    },
  },

  actions: {
    checkLogin() {
      if (!this.user._id || !this.jwt) this.logout()
      if (this.invalidIn && DateTime.fromJSDate(this.invalidIn).diffNow().as('seconds') < 0) {
        this.logout()
        return
      }
      if (!this.invalidIn) {
        this.invalidIn = DateTime.fromJSDate(new Date()).plus({ hours: 72 }).toJSDate()
      }
    },
    async login(userName, password) {
      try {
        const response = await axios.post(`${import.meta.env.VITE_API_URL}auth/login`, {
          username: userName,
          password: password,
        })
        if (response.status === 200) {
          this.user = response.data.user
          this.jwt = response.data.access_token
          this.invalidIn = DateTime.fromJSDate(new Date()).plus({ hours: 72 }).toJSDate()
          window.location.href = '/'
        }
      } catch (error) {
        console.error(error)
      }
    },
    async loadUser() {
      const headers = { Authorization: `Bearer ${this.jwt}` }
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}user/${this.user._id}`, {
          headers,
        })
        this.user = response.data
        if (!this.user?._id) throw Error('Etwas ist schiefgelaufen')
      } catch (error) {
        console.error(error)
        await this.checkForJWTExpired(error)
      }
    },
    async logout() {
      const savedTheme = localStorage.getItem('theme')
      localStorage.clear()
      localStorage.setItem('theme', savedTheme)
      const path = new URL(window.location.href).pathname
      if (path !== '/login') {
        window.location.href = '/login'
      }
    },

    async updateUser() {
      const headers = { Authorization: `Bearer ${this.jwt}` }
      try {
        const response = await axios.put(
          `${import.meta.env.VITE_API_URL}user/${this.user._id}`,
          this.user,
          {
            headers,
          },
        )
        if (response.status === 200 || response.status === 204) {
          const theme = response.data?.theme ?? 'defaultTheme'
          localStorage.setItem('theme', theme)
          this.user = response.data
        }
      } catch (error) {
        console.error(error)
        await this.checkForJWTExpired(error)
      }
    },

    async checkForJWTExpired(errorMessage) {
      if (errorMessage.response.data === 'jwt expired') await this.logout()
    },

    async uploadUserAvatar(image) {
      const headers = { Authorization: `Bearer ${this.jwt}` }
      try {
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}user/avatar/${this.user._id}`,
          image,
          {
            headers,
          },
        )
        if (response.status === 200) {
          this.user.avatar = response.data
        }
      } catch (error) {
        console.error(error)
        await this.checkForJWTExpired(error)
      }
    },
  },
})
